import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ShieldCheck, LifeBuoy, Settings } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SubHeader } from "@/components/Brand";
import { PhotoUploader } from "@/components/PhotoUploader";
import { InstallButton } from "@/components/InstallApp";
import { SoundSettings } from "@/components/SoundSettings";
import { BiometricSettings } from "@/components/BiometricSettings";
import { LoginHistory } from "@/components/LoginHistory";
import { mapsUrl, fmtCoord } from "@/lib/photo";
import { MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate, isStaff, ROLE_LABEL, POSITIONS } from "@/lib/format";
import { AccountNo } from "@/components/AccountNo";
import { PinInput } from "@/components/PinInput";
import { KeyRound } from "lucide-react";

const ChangePin = () => {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const save = async () => {
    if (next !== confirm) {
      setConfirm("");
      return toast.error("Konfirmasi PIN baru tidak sama");
    }
    try {
      await api.post("/auth/pin/change", { current_pin: cur, new_pin: next });
      toast.success("PIN berhasil diganti");
      setOpen(false);
      setCur("");
      setNext("");
      setConfirm("");
    } catch (e) {
      toast.error(errMsg(e));
      setCur("");
      setNext("");
      setConfirm("");
    }
  };
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="change-pin-button" className="w-full flex items-center gap-3 bg-white border border-slate-200 rounded-2xl p-4 font-semibold text-slate-800 hover:bg-orange-50 transition-colors">
        <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 text-white flex items-center justify-center"><KeyRound className="h-4 w-4" /></span> Ganti PIN
      </button>
    );
  }
  return (
    <div className="bg-white border border-orange-200 rounded-2xl p-4 space-y-3" data-testid="change-pin-form">
      <div className="text-sm font-semibold">PIN lama</div>
      <PinInput value={cur} onChange={setCur} testid="change-pin-current" />
      <div className="text-sm font-semibold">PIN baru</div>
      <PinInput value={next} onChange={setNext} autoFocus={false} testid="change-pin-new" />
      <div className="text-sm font-semibold">Ulangi PIN baru</div>
      <PinInput value={confirm} onChange={setConfirm} autoFocus={false} testid="change-pin-confirm" />
      <div className="flex gap-2">
        <button onClick={() => setOpen(false)} className="flex-1 border border-slate-200 py-2.5 rounded-xl text-sm font-semibold" data-testid="change-pin-cancel">Batal</button>
        <button onClick={save} disabled={cur.length !== 6 || next.length !== 6 || confirm.length !== 6} data-testid="change-pin-submit" className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-semibold">Simpan PIN</button>
      </div>
    </div>
  );
};

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ phone: user.phone || "", address: user.address || "", nik: user.nik || "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch("/me/profile", f);
      setUser((u) => ({ ...u, ...r.data }));
      toast.success("Profil disimpan");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <SubHeader title="Profil" />
      <div className="px-5 space-y-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4">
          <PhotoUploader className="h-16 w-16 rounded-full text-xl" testid="profile-photo" />
          <div className="min-w-0">
            <div className="font-bold text-lg truncate" data-testid="profile-name">{user.name}</div>
            <div className="text-xs text-slate-500 truncate" data-testid="profile-email">{user.email}</div>
            <div className="text-xs text-slate-700 mt-0.5">{user.member_no} • Bergabung {fmtDate(user.joined_at)}</div>
            {user.account_no && <div className="text-xs text-slate-700 mt-0.5 flex items-center gap-1">Rek. <AccountNo value={user.account_no} testid="profile-account" /></div>}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3" data-testid="profile-location">
          <MapPin className="h-5 w-5 text-red-600 shrink-0" />
          {user.location ? (
            <div className="text-sm min-w-0">
              <div className="font-semibold">Tikor Anggota</div>
              <a href={mapsUrl(user.location)} target="_blank" rel="noreferrer" className="text-blue-800 underline" data-testid="profile-location-link">{fmtCoord(user.location)}</a>
              <div className="text-[11px] text-slate-500">Dicatat {new Date(user.location.captured_at).toLocaleString("id-ID")} • akurasi ±{Math.round(user.location.accuracy || 0)} m</div>
            </div>
          ) : <div className="text-sm text-slate-600">Tikor belum tercatat. Ketuk foto untuk unggah foto & catat lokasi.</div>}
        </div>

        {user.role === "superadmin" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="profile-position-card">
            <label className="text-sm font-semibold">Jabatan Saya</label>
            <p className="text-[11px] text-slate-500">Tampil di mutasi anggota sebagai "by {user.position || ROLE_LABEL[user.role]}"</p>
            <select data-testid="profile-position-select" value={user.position || ""}
              onChange={async (e) => { try { const r = await api.patch("/me/profile", { position: e.target.value }); setUser((u) => ({ ...u, ...r.data })); toast.success("Jabatan disimpan"); } catch (err) { toast.error(errMsg(err)); } }}
              className="mt-2 w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm">
              <option value="">Tanpa jabatan ({ROLE_LABEL[user.role]})</option>
              {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        )}

        {isStaff(user) && (
          <button onClick={() => navigate("/admin")} data-testid="open-admin-panel"
            className="w-full flex items-center gap-3 bg-gradient-to-r from-blue-700 to-sky-500 hover:brightness-105 text-white rounded-2xl p-4 font-semibold shadow-lg shadow-blue-500/30 transition-[filter]">
            <ShieldCheck className="h-5 w-5" /> Buka Panel {ROLE_LABEL[user.role]}
          </button>
        )}

        <button onClick={() => navigate("/app/bantuan")} data-testid="profile-help-center"
          className="w-full flex items-center gap-3 bg-white border border-slate-200 rounded-2xl p-4 text-left hover:border-emerald-300">
          <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-700 text-white flex items-center justify-center"><LifeBuoy className="h-4 w-4" /></span>
          <span className="flex-1"><span className="block font-semibold text-slate-800">Pusat Bantuan</span><span className="block text-[11px] text-slate-500">Tiket kendala & status penyelesaian</span></span>
        </button>

        <h2 className="font-bold text-slate-900 pt-2 flex items-center gap-2" data-testid="profile-settings-title"><Settings className="h-4 w-4" /> Pengaturan</h2>
        <ChangePin />
        <BiometricSettings />
        <LoginHistory />
        <SoundSettings />

        <InstallButton testid="profile-install-app" />

        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
          <div><label className="text-sm font-semibold">No. HP</label><Input data-testid="profile-phone-input" className="mt-1" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          <div><label className="text-sm font-semibold">NIK</label><Input data-testid="profile-nik-input" className="mt-1" value={f.nik} onChange={(e) => setF({ ...f, nik: e.target.value })} /></div>
          <div><label className="text-sm font-semibold">Alamat</label><Textarea data-testid="profile-address-input" className="mt-1" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
          <button onClick={save} disabled={busy} data-testid="profile-save-button" className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-colors">
            {busy ? "Menyimpan..." : "Simpan Profil"}
          </button>
        </div>

        <button onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-button"
          className="w-full flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 py-3 rounded-xl font-semibold transition-colors">
          <LogOut className="h-5 w-5" /> Keluar
        </button>
      </div>
    </div>
  );
}
